import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { EnvelopeIcon, LockClosedIcon, UserIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/solid';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const benefits = [
  'Free satellite imagery updates',
  'AI-powered crop analysis',
  'Unlimited farm monitoring',
  'Export reports & maps',
];

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created successfully!');
      navigate('/dashboard', { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[45%] relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-kenagri-900 via-kenagri-800 to-kenagri-900" />

        {/* Decorative elements */}
        <div className="absolute inset-0 pattern-grid opacity-20" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-kenagri-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-kenagri-400/10 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          {/* Logo */}
          <div className="flex items-center mb-12">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-kenagri-400 to-kenagri-600 shadow-glow-green">
              <span className="text-2xl">🛰️</span>
            </div>
            <span className="ml-4 text-3xl font-bold text-white tracking-tight">KENAGRI</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
            Start Monitoring
            <br />
            <span className="text-kenagri-300">Your Farms Today</span>
          </h1>

          <p className="text-lg text-white/70 mb-10 max-w-md">
            Join thousands of farmers using satellite technology to optimize their crop production.
          </p>

          {/* Benefits list */}
          <ul className="space-y-4">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-center text-white/80">
                <CheckCircleIcon className="h-5 w-5 text-kenagri-400 mr-3 flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          {/* Trust badge */}
          <div className="mt-12 flex items-center gap-3 text-white/60">
            <ShieldCheckIcon className="h-6 w-6 text-kenagri-400" />
            <span className="text-sm">Your data is secure and encrypted</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-kenagri-500 to-kenagri-600 shadow-glow-green">
                <span className="text-xl">🛰️</span>
              </div>
              <span className="ml-3 text-2xl font-bold text-gray-900">KENAGRI</span>
            </div>
          </div>

          {/* Form header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Create your account</h2>
            <p className="mt-2 text-gray-600">Get started with free satellite monitoring</p>
          </div>

          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Full name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              autoComplete="name"
              leftIcon={<UserIcon className="h-5 w-5" />}
            />

            <Input
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              leftIcon={<EnvelopeIcon className="h-5 w-5" />}
            />

            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              autoComplete="new-password"
              hint="At least 6 characters"
              leftIcon={<LockClosedIcon className="h-5 w-5" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              }
            />

            <Input
              label="Confirm password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              autoComplete="new-password"
              leftIcon={<LockClosedIcon className="h-5 w-5" />}
            />

            <div className="flex items-start">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="h-4 w-4 mt-0.5 text-kenagri-600 border-gray-300 rounded focus:ring-kenagri-500 transition-colors"
              />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                I agree to the{' '}
                <a href="#" className="text-kenagri-600 hover:text-kenagri-700 font-medium">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-kenagri-600 hover:text-kenagri-700 font-medium">Privacy Policy</a>
              </label>
            </div>

            <Button
              type="submit"
              loading={isLoading}
              fullWidth
              size="lg"
            >
              Create account
            </Button>
          </form>

          {/* Sign in link */}
          <p className="mt-8 text-center text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-kenagri-600 hover:text-kenagri-700 font-semibold transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
